/**
 * GravityPullSystem — centralized gravity-well management.
 *
 * Multiple Astral nodes create gravitational pull zones: Rift, Implosion,
 * Anchor, Event Horizon, Dead Star, Meteo distortion zones, and Collapse.
 * Rather than each skill implementing its own pull-vector math, this system
 * manages every active gravity well in one place.
 *
 * Each well has a radius and pull strength. Per frame, update() walks all
 * enemies, computes each well's contribution via an inverse-distance falloff
 * (linear: full strength at centre, zero at edge), sums the vectors, and
 * applies the resulting displacement. Wells expire automatically when their
 * duration elapses.
 *
 * The system is intentionally physics-agnostic — it writes directly to
 * entity x/y positions via the EntityPositionAdapter interface, keeping
 * Phaser Body APIs out of the system's core.
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see astral-spec.md § GravityPullSystem
 * @see design-decisions.md § Astral Base Values (pull radii / strengths)
 * @see ADR-064 (attunement shared systems architecture)
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Configuration for a single gravity well. */
export interface GravityWellConfig {
  id: string;
  position: { x: number; y: number };
  /** Effective pull radius in tiles. */
  pullRadiusTiles: number;
  /** Pull strength in tiles per second at the well's centre. */
  pullStrengthTilesPerSec: number;
  /** Duration in ms. -1 = permanent (cleared manually). */
  durationMs: number;
  sourceSkillId: string;
}

/**
 * Callback invoked when a well expires naturally (duration elapsed).
 * Not called when a well is removed via removeWell().
 */
export type WellExpiryCallback = (
  wellId: string,
  position: { x: number; y: number },
  sourceSkillId: string,
) => void;

/** Internal runtime entry. */
interface GravityWellEntry extends GravityWellConfig {
  remainingMs: number;
}

/** Minimal entity interface required by update(). */
export interface GravityEntity {
  id: string;
  x: number;
  y: number;
}

/** Callback contract to reposition entities after pull computation. */
export interface GravityPositionWriter {
  setPosition(entityId: string, x: number, y: number): void;
}

// ---------------------------------------------------------------------------
// System
// ---------------------------------------------------------------------------

import { TILE_SIZE } from '../utils/Constants.js';


export class GravityPullSystem {
  private readonly _wells = new Map<string, GravityWellEntry>();
  private readonly _expiryCallbacks: WellExpiryCallback[] = [];

  // ---------------------------------------------------------------------------
  // Well management
  // ---------------------------------------------------------------------------

  /**
   * Register a gravity well.
   * @returns The well's id (same as config.id for easy removal).
   */
  addWell(config: GravityWellConfig): string {
    this._wells.set(config.id, {
      ...config,
      remainingMs: config.durationMs,
    });
    return config.id;
  }

  /** Remove a gravity well before it naturally expires. */
  removeWell(wellId: string): void {
    this._wells.delete(wellId);
  }

  /** Read-only snapshot of all active gravity wells. */
  getAll(): ReadonlyArray<Readonly<GravityWellConfig>> {
    return Array.from(this._wells.values());
  }

  /**
   * Subscribe to natural well expiration events.
   *
   * The callback fires when a well's duration elapses, NOT when it is
   * removed via removeWell(). This lets Tremor Anchor distinguish
   * natural expiry (detonate) from manual cancellation (no detonation).
   *
   * @returns Unsubscribe function — call to stop receiving callbacks.
   */
  onWellExpired(callback: WellExpiryCallback): () => void {
    this._expiryCallbacks.push(callback);
    return () => {
      const idx = this._expiryCallbacks.indexOf(callback);
      if (idx >= 0) this._expiryCallbacks.splice(idx, 1);
    };
  }

  // ---------------------------------------------------------------------------
  // Vector query
  // ---------------------------------------------------------------------------

  /**
   * Compute the net pull vector (in pixels) at world position (x, y)
   * as the sum of all in-range active wells for one frame of delta ms.
   *
   * Each well applies linear falloff: full strength at the centre,
   * zero at the edge.
   *
   * @returns Pixel displacement vector for this frame.
   */
  getPullVector(x: number, y: number, deltaMs: number): { dx: number; dy: number } {
    let dx = 0;
    let dy = 0;

    for (const well of this._wells.values()) {
      const wx = well.position.x;
      const wy = well.position.y;
      const distPx = Math.sqrt((x - wx) ** 2 + (y - wy) ** 2);
      const radiusPx = well.pullRadiusTiles * TILE_SIZE;

      if (distPx >= radiusPx || distPx < 0.001) continue;

      // Linear falloff: (1 - dist/radius)
      const falloff = 1 - distPx / radiusPx;
      // Strength in pixels per ms
      const strengthPxPerMs = (well.pullStrengthTilesPerSec * TILE_SIZE) / 1000;
      const force = falloff * strengthPxPerMs * deltaMs;

      // Direction from entity toward well centre
      const nx = (wx - x) / distPx;
      const ny = (wy - y) / distPx;

      dx += nx * force;
      dy += ny * force;
    }

    return { dx, dy };
  }

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  /**
   * Per-frame: apply pull vectors to all entities, expire elapsed wells.
   *
   * @param delta       Frame delta in ms.
   * @param entities    All entities to pull (typically all enemies in scene).
   * @param writer      Callback that commits new positions.
   */
  update(delta: number, entities: Iterable<GravityEntity>, writer: GravityPositionWriter): void {
    // Apply pulls
    for (const entity of entities) {
      const { dx, dy } = this.getPullVector(entity.x, entity.y, delta);
      if (dx !== 0 || dy !== 0) {
        writer.setPosition(entity.id, entity.x + dx, entity.y + dy);
      }
    }

    // Expire wells
    for (const [id, well] of this._wells) {
      if (well.durationMs === -1) continue; // permanent
      well.remainingMs -= delta;
      if (well.remainingMs <= 0) {
        // Fire natural-expiry callbacks BEFORE removing the entry so listeners
        // can still query getAll() if needed (the expiring well will be absent
        // from subsequent getAll() calls after this frame).
        for (const cb of this._expiryCallbacks) {
          cb(id, { ...well.position }, well.sourceSkillId);
        }
        this._wells.delete(id);
      }
    }
  }

  /** Remove all active wells. */
  destroy(): void {
    this._wells.clear();
  }
}
